/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';
import SelectWidget from './SelectWidget';
import { fontSizes } from '../../pages/LayoutEditorPage/components/StoryHelpers';
import IconButton from 'shared/buttons/IconButton';
import Accordion from 'shared/Accordion';
import { get } from 'lodash';
import ColorWidget from './ColorWidget';
import HorizontalAlignWidget from './HorizontalAlignWidget';
import { GenericInputProps, InputChangeParams } from 'shared/types/commonPropTypes';
import { richtextStyles } from 'shared/editor/PublishHelper';

export default function FontStyler(props: GenericInputProps) {
  const { aggregatedOptions, option } = props;

  const fields = option?.custom?.fields ?? {
    bold: 1,
    italic: 1,
    color: 1,
    fontSize: 1,
    align: 1
  };

  const getAggregatedLabels = () => {
    const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

    const style = {
      bold: (fields.bold && extractValue(aggregatedOptions, '', 'fontWeight')) || '',
      italic: (fields.italic && extractValue(aggregatedOptions, '', 'fontStyle')) || '',
      textSize: (fields.fontSize && extractValue(aggregatedOptions, '', 'fontSize')) || '',
      textAlign: (fields.align && extractValue(aggregatedOptions, '', 'textAlign')) || '',
      color: (fields.color && extractValue(aggregatedOptions, '', 'color')) || ''
    };

    return (Object.keys(style) as Array<keyof typeof style>)
      .map((key) => {
        return capitalizeFirstLetter(
          ((style[key] === 'unset' || style[key] === '' ? '' : style[key]) ?? '').toString()
        );
      })
      .filter((d) => d !== '')
      .join(', ');
  };

  const onFontSizeChange = (params: InputChangeParams) => {
    const { val } = params;
    props.onChange({
      val: val + 'px',
      optional: {
        id: `${option?.id}.fontSize`
      }
    });
  };

  const onChangeBold = () => {
    const { aggregatedOptions, option } = props;
    const prevVal = get(aggregatedOptions, `${option?.id}.fontWeight`);

    if (prevVal === 'bold') {
      props.onChange({
        val: 'unset',
        optional: {
          id: `${option?.id}.fontWeight`
        }
      });
    } else {
      props.onChange({
        val: 'bold',
        optional: {
          id: `${option?.id}.fontWeight`
        }
      });
    }
  };

  const onChangeItalic = () => {
    const { aggregatedOptions, option } = props;
    const prevVal = get(aggregatedOptions, `${option?.id}.fontStyle`);

    if (prevVal === 'italic') {
      props.onChange({
        val: 'unset',
        optional: {
          id: `${option?.id}.fontStyle`
        }
      });
    } else {
      props.onChange({
        val: 'italic',
        optional: {
          id: `${option?.id}.fontStyle`
        }
      });
    }
  };

  const onChangeColor = (params: InputChangeParams) => {
    props.onChange({
      val: params.val,
      optional: {
        id: `${option?.id}.color`
      }
    });
  };

  const extractValue = (aggregatedOptions: any, defaultVal: string, property: string) => {
    return get(aggregatedOptions, `${option?.id}.${property}`) ?? defaultVal;
  };

  const onChangeAlign = (params: InputChangeParams) => {
    props.onChange({
      val: params.val,
      optional: {
        id: `${option?.id}.textAlign`
      }
    });
  };

  return (
    <Accordion
      key={'key'}
      useInternalSelect={true}
      headerText={props.label}
      smallHeaderText={true}
      selectedChildren={getAggregatedLabels()}
      defaultSelection={false}
      accordionClasses={'bg-white'}
      childrenClasses={'relative flex flex-col rounded mx-4 py-2'}
    >
      <div className="flex flex-row flex-wrap items-center gap-1">
        {fields.bold && (
          <IconButton
            icon="bold"
            className="w-8 h-8 rounded "
            onClick={onChangeBold}
            active={extractValue(aggregatedOptions, '', 'fontWeight') === 'bold'}
          />
        )}
        {fields.italic && (
          <IconButton
            icon="italic"
            className="w-8 h-8 rounded"
            onClick={onChangeItalic}
            active={extractValue(aggregatedOptions, '', 'fontStyle') === 'italic'}
          />
        )}

        {fields.fontSize && (
          <SelectWidget
            selectOptions={fontSizes}
            onChange={onFontSizeChange}
            value={parseInt(extractValue(aggregatedOptions, '18', 'fontSize'), 10).toString()}
            customStyles={richtextStyles}
            inlineSelect={true}
          />
        )}

        {fields.color && <ColorWidget value={extractValue(aggregatedOptions, '', 'color')} onChange={onChangeColor} />}

        {fields.align && (
          <HorizontalAlignWidget
            className={'inline-block '}
            onChange={onChangeAlign}
            value={extractValue(aggregatedOptions, '', 'textAlign')}
          />
        )}
      </div>
    </Accordion>
  );
}
